// components/AnalyticsDashboard.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiUsers,
  FiAward,
  FiBarChart2,
  FiPieChart
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe', '#00C49F'];

function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://firstaidba.vercel.app/api/user/admin/analytics', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
        <p>{error}</p>
      </div>
    );
  }

  if (!analytics) return null;

  // Prepare data for charts
  const barChartData = analytics.scenarioData
    .filter(scenario => scenario.attempts > 0)
    .map(scenario => ({
      name: scenario.title,
      AverageScore: scenario.averageScore,
      Attempts: scenario.attempts,
      CompletionRate: scenario.completionRate
    }));

  const pieChartData = analytics.mostChallenging.map(scenario => ({
    name: scenario.title,
    value: scenario.averageScore
  }));

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiUsers className="text-blue-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-800">{analytics.userStats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <FiAward className="text-green-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-gray-800">{analytics.userStats.usersWithAttempts}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <FiTrendingUp className="text-purple-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Participation Rate</p>
              <p className="text-2xl font-bold text-gray-800">{analytics.userStats.participationRate}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <FiBarChart2 className="text-orange-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Attempts</p>
              <p className="text-2xl font-bold text-gray-800">
                {analytics.scenarioData.reduce((sum, scenario) => sum + scenario.attempts, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance by Scenario */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <FiBarChart2 className="text-purple-600" />
            <h3 className="text-lg font-semibold">Performance by Scenario</h3>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="AverageScore" fill="#8884d8" name="Average Score (%)" />
                <Bar dataKey="CompletionRate" fill="#82ca9d" name="Completion Rate (%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Most Challenging Scenarios */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <FiTrendingDown className="text-red-600" />
            <h3 className="text-lg font-semibold">Most Challenging Scenarios</h3>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}%`}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detailed Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Challenging Scenarios Table */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <FiTrendingDown className="text-red-600" />
            <h3 className="text-lg font-semibold">Top 5 Most Challenging Scenarios</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 font-semibold text-gray-700">Scenario</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Avg Score</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Attempts</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Completion</th>
                </tr>
              </thead>
              <tbody>
                {analytics.mostChallenging.map((scenario, index) => (
                  <tr key={scenario.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {scenario.title}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 py-1 px-2 rounded-full text-sm ${
                        scenario.averageScore >= 80 ? 'bg-green-100 text-green-800' :
                        scenario.averageScore >= 60 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {scenario.averageScore}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{scenario.attempts}</td>
                    <td className="px-4 py-3 text-gray-700">{scenario.completionRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Most Successful Scenarios Table */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <FiTrendingUp className="text-green-600" />
            <h3 className="text-lg font-semibold">Top 5 Most Successful Scenarios</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 font-semibold text-gray-700">Scenario</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Avg Score</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Attempts</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Completion</th>
                </tr>
              </thead>
              <tbody>
                {analytics.mostSuccessful.map((scenario, index) => (
                  <tr key={scenario.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {scenario.title}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 py-1 px-2 rounded-full text-sm ${
                        scenario.averageScore >= 80 ? 'bg-green-100 text-green-800' :
                        scenario.averageScore >= 60 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {scenario.averageScore}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{scenario.attempts}</td>
                    <td className="px-4 py-3 text-gray-700">{scenario.completionRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Scenario Participation Chart */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <FiPieChart className="text-blue-600" />
          <h3 className="text-lg font-semibold">Scenario Participation</h3>
        </div>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={analytics.scenarioData.filter(s => s.attempts > 0)}
              layout="vertical"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="title" width={150} />
              <Tooltip />
              <Legend />
              <Bar dataKey="attempts" fill="#8884d8" name="Number of Attempts" />
              <Bar dataKey="usersCompleted" fill="#82ca9d" name="Users Completed" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsDashboard;